import React, { useContext, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import Alert from '@r1-frontend/ui-react/components/Alert';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { InfoCircle, TickCircle } from '@r1-frontend/ui-react/components/svg/main';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { FullBuyContext, TFullBuyContextProperties } from '~/src/features/full-buy/analytics/context';
import CallbackContacts from '~/src/features/full-buy/CallbackContacts';
import { MainLayout } from '~/src/features/full-buy/containers/MainLayout/MainLayout';
import EmployeeVisit from '~/src/features/full-buy/EmployeeVisit';
import { isHaveSelectedProducts } from '~/src/features/full-buy/store/selectors/products/isHaveSelectedProducts';
import TariffInclude from '~/src/features/full-buy/TariffInclude';
import { TState } from '~/src/store';

import * as S from '~/src/features/full-buy/containers/pages/styled';

export const Final = (): JSX.Element => {
    const { isVillage } = useContext<TFullBuyContextProperties>(FullBuyContext);
    const isLaptop = useMatchMediaByWidth(wideBreakpoints.laptop);

    const {
        agreementData,
        hasProducts,
        slot,
    } = useSelector((state: TState) => ({
        agreementData: state.fullBuy.agreement,
        hasProducts: isHaveSelectedProducts(state),
        slot: state.fullBuy.slot,
    }), shallowEqual);

    const dateTime = useMemo(() => {
        const [hour, minutes] = slot?.hour.split(':') || ['00', '00'];
        return `${slot?.date} c ${slot?.hour} по ${String(Number(hour) + 1).padStart(2, '0')}:${minutes}`;
    }, [slot?.date, slot?.hour]);

    return (
        <MainLayout showFinalTicket>
            <S.StageWrap>
                {agreementData.isDouble &&
                    <Alert Icon={InfoCircle} type={'error'}>
                        <Paragraph4>
                            К&nbsp;вашим данным уже привязан другой договор. Скоро позвоним, чтобы во&nbsp;всём разобраться.{'\n'}
                            Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.
                        </Paragraph4>
                    </Alert>
                }
                {agreementData.hasError && !agreementData.isDouble &&
                    <Alert Icon={InfoCircle} type={'error'}>
                        <Paragraph4>
                            Не&nbsp;удалось создать договор. Скоро позвоним, чтобы во&nbsp;всём разобраться.{'\n'}
                            Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.
                        </Paragraph4>
                    </Alert>
                }
                {!agreementData.hasError &&
                    <>
                        <Alert Icon={TickCircle} type={'success'}>
                            <Paragraph4>
                                Ваша заявка создана и&nbsp;подтверждена. Мы&nbsp;не&nbsp;будем беспокоить вас звонками,
                                всю информацию вы&nbsp;получите в&nbsp;смс-сообщении.
                            </Paragraph4>
                        </Alert>
                        <EmployeeVisit
                            date={dateTime}
                            sla={isVillage ? '3 часов' : '60 мин'}
                            isInstallationHardware={hasProducts}
                        />
                    </>
                }
                {!isLaptop &&
                    <>
                        <TariffInclude showLink={!!dateTime}/>
                        <H3>Если остались вопросы</H3>
                        <CallbackContacts/>
                    </>
                }
            </S.StageWrap>
        </MainLayout>
    );
};
